import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';

import DataTable from '../../../sharedModules/dataTable';
import CustomerIssuesFilter from '../components/CustomerIssuesFilter';

import { CustomerIssue } from '../../model';
import { customerIssuesColumns } from '../constants/customerIssuesColumns';

import axios from '../../../sharedModules/utils/axios.utils';

const defaultFilterValues = {
  resolved_eq: 'N',
  db_location_eq: 'all',
};

const CustomerIssuesListPage: React.FunctionComponent = () => {
  const history = useHistory();

  const [customerIssues, setCustomerIssues] = useState<CustomerIssue[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [filterValues, setFilterValues] = useState<any>(defaultFilterValues);
  const [stats, setStats] = useState<any>(null);

  const fetchCustomerIssues = async (fetchFilterValues = filterValues) => {
    setLoading(true);
    try {
      const response = await axios.get<string, any>('/customer_issues/servman_customer_issues', {
        params: {
          'ransack[resolved_eq]': fetchFilterValues.resolved_eq !== 'all' ? fetchFilterValues.resolved_eq : undefined,
          'ransack[db_location_eq]':
            fetchFilterValues.db_location_eq !== 'all' ? fetchFilterValues.db_location_eq : undefined,
        },
      });
      setCustomerIssues(response?.data?.result || []);
    } catch (error) {
      console.error('Error fetching customer issues:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get<string, any>('/customer_issues/servman_customer_issues/stats');
      setStats(response?.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post('/customer_issues/servman_customer_issues/sync');
      await fetchCustomerIssues();
      await fetchStats();
      alert('Customer issues synced successfully!');
    } catch (error) {
      console.error('Error syncing customer issues:', error);
      alert('Failed to sync customer issues. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchCustomerIssues(defaultFilterValues);
    fetchStats();
  }, []);

  const columns = customerIssuesColumns();

  const handleRowClick = (row: CustomerIssue) => {
    history.push(`/customer_issues/${row.recno}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssignmentLateIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                Customer Issues
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Track and manage customer issues from ServiceMan calendar system
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={handleSync}
            disabled={syncing}
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
              },
            }}
          >
            {syncing ? 'Syncing...' : 'Sync from ServMan'}
          </Button>
        </Box>
        <Divider />
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 2,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'primary.lighter',
                    display: 'flex',
                    mr: 2,
                  }}
                >
                  <AssignmentLateIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h3" fontWeight="bold" color="primary.main">
                    {stats.total}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Total Issues
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'warning.main',
                  boxShadow: 2,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'warning.lighter',
                    display: 'flex',
                    mr: 2,
                  }}
                >
                  <WarningIcon sx={{ color: 'warning.main', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h3" fontWeight="bold" color="warning.main">
                    {stats.unresolved}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Unresolved
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'success.main',
                  boxShadow: 2,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'success.lighter',
                    display: 'flex',
                    mr: 2,
                  }}
                >
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h3" fontWeight="bold" color="success.main">
                    {stats.resolved}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Resolved
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <CustomerIssuesFilter
        filterValues={filterValues}
        setFilterValues={setFilterValues}
        onFilter={fetchCustomerIssues}
      />

      {/* Data Table */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <DataTable
            columns={columns}
            data={customerIssues}
            onRowClicked={handleRowClick}
            progressPending={loading}
            highlightOnHover
            pointerOnHover
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            dense
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomerIssuesListPage;
